import React, { useState, useCallback, useEffect } from 'react';
import { Box, Slider, IconButton, Tooltip } from '@mui/material';
import GestureIcon from '@mui/icons-material/Gesture';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import UndoIcon from '@mui/icons-material/Undo';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TimelineIcon from '@mui/icons-material/Timeline';
import SaveIcon from '@mui/icons-material/Save';
import { useAnnotation } from './diagnosticDesign/hooks/useAnnotation';

const ScreenshotViewer = ({ src, onSave }) => {
    const [gallery, setGallery] = useState([{ url: src, label: 'Screenshot' }]);
    const [galleryTab] = useState('annotate');

    useEffect(() => { setGallery([{ url: src, label: 'Screenshot' }]); }, [src]);

    const ann = useAnnotation(galleryTab, gallery, 0, gallery, setGallery);

    const prevLen = React.useRef(gallery.length);
    useEffect(() => {
        if (gallery.length > prevLen.current && onSave) {
            const last = gallery[gallery.length - 1];
            onSave(last.url);
        }
        prevLen.current = gallery.length;
    }, [gallery, onSave]);

    const toolBtnSx = (active) => ({
        color: active ? '#c9a0dc' : 'rgba(255,255,255,0.4)',
        backgroundColor: active ? 'rgba(201,160,220,0.15)' : 'transparent',
        border: active ? '1px solid rgba(201,160,220,0.3)' : '1px solid transparent',
        p: 0.5,
    });

    const isToolActive = (tool) =>
        ann.drawTool === tool && !ann.annotateEraser && !ann.annotateZoomMode && !ann.annotateNoteMode && !ann.annotateCurveMode;

    const selectTool = (tool) => {
        ann.setDrawTool(tool);
        ann.setAnnotateEraser(false);
        ann.setAnnotateZoomMode(false);
        ann.setAnnotateNoteMode(false);
        ann.setAnnotateCurveMode(false);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', overflow: 'hidden' }}>
            {/* Toolbar */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 0.75,
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                bgcolor: '#12122a', flexShrink: 0, flexWrap: 'wrap',
            }}>
                {/* Draw tools */}
                <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center', borderRight: '1px solid rgba(255,255,255,0.12)', pr: 1 }}>
                    {([
                        { tool: 'pen', icon: <GestureIcon sx={{ fontSize: 18 }} />, label: 'Pen' },
                        { tool: 'line', icon: <HorizontalRuleIcon sx={{ fontSize: 18 }} />, label: 'Line' },
                        { tool: 'arrow', icon: <ArrowRightAltIcon sx={{ fontSize: 18 }} />, label: 'Arrow' },
                        { tool: 'rect', icon: <CropSquareIcon sx={{ fontSize: 18 }} />, label: 'Rectangle' },
                        { tool: 'circle', icon: <RadioButtonUncheckedIcon sx={{ fontSize: 18 }} />, label: 'Circle' },
                    ]).map(({ tool, icon, label }) => (
                        <Tooltip key={tool} title={label}>
                            <IconButton size="small" onClick={() => selectTool(tool)} sx={toolBtnSx(isToolActive(tool))}>
                                {icon}
                            </IconButton>
                        </Tooltip>
                    ))}
                </Box>

                {/* Colors */}
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {['#ff0000', '#ff9800', '#ffeb3b', '#4caf50', '#2196f3', '#9c27b0', '#ffffff', '#000000'].map(c => (
                        <Box key={c} onClick={() => { ann.setAnnotateBrushColor(c); ann.setAnnotateEraser(false); }}
                            sx={{
                                width: 18, height: 18, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                                border: ann.annotateBrushColor === c && !ann.annotateEraser ? '2px solid #c9a0dc' : '2px solid rgba(255,255,255,0.15)',
                                '&:hover': { transform: 'scale(1.2)' }, transition: 'transform 0.15s',
                            }} />
                    ))}
                </Box>

                {/* Brush size */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>Size</Box>
                    <Slider value={ann.annotateBrushSize} onChange={(_, v) => ann.setAnnotateBrushSize(v)}
                        min={1} max={20} step={1} size="small"
                        sx={{ width: 70, color: 'rgba(255,255,255,0.4)', '& .MuiSlider-thumb': { width: 10, height: 10, bgcolor: '#c9a0dc' } }} />
                    <Box sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, minWidth: 14 }}>{ann.annotateBrushSize}</Box>
                </Box>

                {/* Eraser */}
                <Tooltip title="Eraser">
                    <IconButton size="small" onClick={() => ann.setAnnotateEraser(p => !p)} sx={toolBtnSx(ann.annotateEraser)}>
                        <AutoFixHighIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                {/* Undo */}
                <Tooltip title="Undo">
                    <IconButton size="small" onClick={ann.undo} sx={{ color: 'rgba(255,255,255,0.4)', p: 0.5 }}>
                        <UndoIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                {/* Note */}
                <Tooltip title={ann.annotateNoteMode ? 'Click to place note' : 'Add note'}>
                    <IconButton size="small" onClick={() => { ann.setAnnotateNoteMode(p => !p); ann.setAnnotateZoomMode(false); ann.setAnnotateCurveMode(false); }}
                        sx={toolBtnSx(ann.annotateNoteMode)}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                {/* Bezier curve */}
                <Tooltip title={ann.annotateCurveMode ? 'Exit curve mode' : 'Bezier curve'}>
                    <IconButton size="small" onClick={() => {
                        const entering = !ann.annotateCurveMode;
                        ann.setAnnotateCurveMode(entering);
                        if (entering) { ann.setAnnotateNoteMode(false); ann.setAnnotateZoomMode(false); ann.setAnnotateEraser(false); }
                        else ann.setAnnotateCurvePoints([]);
                    }} sx={toolBtnSx(ann.annotateCurveMode)}>
                        <TimelineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                {ann.annotateCurveMode && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box onClick={() => ann.setAnnotateCurveClosed(p => !p)} sx={{
                            fontSize: 10, cursor: 'pointer', px: 0.75, py: 0.25, borderRadius: 1, userSelect: 'none',
                            color: ann.annotateCurveClosed ? '#c9a0dc' : 'rgba(255,255,255,0.4)',
                            border: `1px solid ${ann.annotateCurveClosed ? 'rgba(201,160,220,0.3)' : 'rgba(255,255,255,0.12)'}`,
                        }}>{ann.annotateCurveClosed ? 'Closed' : 'Open'}</Box>
                        <Box sx={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{ann.annotateCurvePoints.length} pts</Box>
                        {ann.annotateCurvePoints.length > 0 && (
                            <Tooltip title="Remove last point">
                                <IconButton size="small" onClick={ann.annotateCurveRemoveLast} sx={{ color: 'rgba(255,255,255,0.4)', p: 0.25 }}>
                                    <UndoIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {ann.annotateCurvePoints.length >= 2 && (
                            <Tooltip title="Apply curve">
                                <IconButton size="small" onClick={ann.annotateCurveApply} sx={{ color: '#4caf50', p: 0.25 }}>
                                    <SaveIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                )}

                {/* Save */}
                <Tooltip title="Save annotated image">
                    <IconButton size="small" onClick={ann.save} sx={{ color: '#4caf50', p: 0.5, ml: 'auto' }}>
                        <SaveIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Canvas area */}
            <Box ref={ann.annotateContainerRef} sx={{ flex: 1, position: 'relative', overflow: 'hidden', bgcolor: '#0a0a14', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={ann.annotateNoteMode ? ann.addNote : ann.annotateCurveMode ? ann.annotateCurveAddPoint : undefined}
            >
                <canvas ref={ann.annotateCanvasRef}
                    style={{
                        maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block',
                        cursor: ann.annotateNoteMode ? 'crosshair' : ann.annotateCurveMode ? 'crosshair' :
                            ann.annotateEraser ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${ann.annotateBrushSize * 2 + 4}' height='${ann.annotateBrushSize * 2 + 4}'%3E%3Ccircle cx='${ann.annotateBrushSize + 2}' cy='${ann.annotateBrushSize + 2}' r='${ann.annotateBrushSize}' fill='none' stroke='white' stroke-width='1'/%3E%3C/svg%3E") ${ann.annotateBrushSize + 2} ${ann.annotateBrushSize + 2}, auto` : 'crosshair',
                    }}
                    onMouseDown={ann.startDraw}
                    onMouseMove={ann.draw}
                    onMouseUp={ann.endDraw}
                    onMouseLeave={ann.endDraw}
                />

                {/* Bezier curve SVG overlay */}
                {ann.annotateCurveMode && ann.annotateCurvePoints.length > 0 && ann.annotateCanvasRect && (
                    <svg style={{
                        position: 'absolute',
                        left: ann.annotateCanvasRect.left ?? 0, top: ann.annotateCanvasRect.top ?? 0,
                        width: ann.annotateCanvasRect.width, height: ann.annotateCanvasRect.height,
                        pointerEvents: 'none',
                    }}>
                        {ann.annotateCurvePoints.length >= 2 && (
                            <path d={ann.generateCurvePath(ann.annotateCurvePoints, ann.annotateCanvasRect.width, ann.annotateCanvasRect.height, ann.annotateCurveClosed)}
                                stroke={ann.annotateBrushColor} strokeWidth={ann.annotateBrushSize} fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        )}
                        {ann.annotateCurvePoints.map((pt, i) => (
                            <g key={i} style={{ pointerEvents: 'all', cursor: 'grab' }}
                                onMouseDown={e => { e.stopPropagation(); ann.annotateCurveDragging.current = i; ann.annotateCurveDragUsed.current = false; }}
                            >
                                <circle cx={pt.x * ann.annotateCanvasRect.width} cy={pt.y * ann.annotateCanvasRect.height} r={6}
                                    fill={ann.annotateBrushColor} stroke="#fff" strokeWidth={1.5} />
                                <text x={pt.x * ann.annotateCanvasRect.width} y={pt.y * ann.annotateCanvasRect.height - 10}
                                    textAnchor="middle" fill="#fff" fontSize={9} fontWeight="bold">{i + 1}</text>
                            </g>
                        ))}
                    </svg>
                )}

                {/* Notes overlay */}
                {ann.annotateNotes.length > 0 && ann.annotateCanvasRect && (
                    <Box sx={{
                        position: 'absolute',
                        left: ann.annotateCanvasRect.left ?? 0, top: ann.annotateCanvasRect.top ?? 0,
                        width: ann.annotateCanvasRect.width, height: ann.annotateCanvasRect.height,
                        pointerEvents: 'none',
                    }}>
                        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                            {ann.annotateNotes.map((note, i) => (
                                <React.Fragment key={i}>
                                    <line x1={`${note.px * 100}%`} y1={`${note.py * 100}%`} x2={`${note.tx * 100}%`} y2={`${note.ty * 100}%`}
                                        stroke="#fff" strokeWidth="1.5" strokeDasharray="4 2" />
                                    <circle cx={`${note.px * 100}%`} cy={`${note.py * 100}%`} r="5" fill="#c9a0dc" stroke="#fff" strokeWidth="1" />
                                </React.Fragment>
                            ))}
                        </svg>
                        {ann.annotateNotes.map((note, i) => (
                            <Box key={i} sx={{
                                position: 'absolute', left: `${note.tx * 100}%`, top: `${note.ty * 100}%`,
                                transform: 'translate(-50%, -100%)', pointerEvents: 'all', cursor: 'move',
                                background: 'rgba(0,0,0,0.75)', border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '4px', px: 0.75, py: 0.25, minWidth: 30,
                            }}
                                onMouseDown={e => { e.stopPropagation(); ann.annotateDraggingNote.current = { idx: i, startX: e.clientX, startY: e.clientY }; }}
                            >
                                {ann.annotateEditingNote === i ? (
                                    <input autoFocus value={note.text}
                                        onChange={e => ann.setAnnotateNotes(prev => prev.map((n, j) => j === i ? { ...n, text: e.target.value } : n))}
                                        onBlur={() => ann.setAnnotateEditingNote(null)}
                                        onKeyDown={e => { if (e.key === 'Enter') ann.setAnnotateEditingNote(null); }}
                                        style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 12, outline: 'none', width: 80 }} />
                                ) : (
                                    <Box onClick={e => { e.stopPropagation(); ann.setAnnotateEditingNote(i); }}
                                        sx={{ color: '#fff', fontSize: 12, minHeight: 16, cursor: 'text' }}>
                                        {note.text || <span style={{ color: 'rgba(255,255,255,0.3)' }}>Click to type...</span>}
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ScreenshotViewer;
